import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useDrag } from 'react-dnd';
import _truncate from 'lodash/truncate';

import handleKeyPress from '../../../common/handleKeyPress';
import { useAddBlock } from '../../../common/useAddBlock';
import { TAppState } from '../../../reducers';
import { getColorModifications, hexToHSL, hexToHSV, hexToRGB } from '../../../common/colorUtils';
import * as itemTypes from '../../../constants/itemTypes/itemTypes';
import * as imageSources from '../../../constants/imageSources/imageSources';
import * as Styled from './styles';
import rules from '../../../constants/businessRules/businessRules';

const mapStateToProps = (state: TAppState) => ({
    image: state.thirdPartyContent.staffbase.config?.logoUrl,
    appName: state.thirdPartyContent.staffbase.config?.appName,
    colors: state.thirdPartyContent.staffbase.config?.colors,
    lastBlock: state.canvasBlocks.present[state.canvasBlocks.present.length - 1],
});

const connector = connect(mapStateToProps);

interface IOwnProps {
    createBlockContent: () => any;
}

type TProps = IOwnProps & ConnectedProps<typeof connector>;

const DraggableSBBlock: FC<TProps> = ({ image, appName, colors, lastBlock, createBlockContent }) => {
    const [isOver, setIsOver] = useState(false);
    const [modifications, setModifications] = useState({ rgba: '', filter: '', hoverBorderColor: '' });
    const dispatch = useDispatch();
    const addBlock = useAddBlock({ lastBlock, generateBlock: createBlockContent, dispatch });
    const logo = image || imageSources.default.STAFFBASE.LOGO;

    useEffect(() => {
        const { saturation, value } = hexToHSV(colors?.backgroundColor || '#FFF');
        const { hue, saturation: hslSat, lightness } = hexToHSL(colors?.backgroundColor || '#FFF');
        const { r, g, b } = hexToRGB(colors?.backgroundColor || '#FFF');
        const { opacity, filter } = getColorModifications(saturation, value);
        const border = opacity === 1 ? `hsl(${hue}, ${hslSat}%, ${lightness - 25}%)` : colors?.backgroundColor;
        setModifications({ rgba: `rgba(${r},${g},${b},${opacity})`, filter, hoverBorderColor: border || '' });
    }, [colors?.backgroundColor]);

    const [{ isDragging, handlerId }, drag] = useDrag(() => ({
        type: itemTypes.STATIC_BLOCK,
        item: () => {
            const newBlock = createBlockContent();
            return { newBlock };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
            handlerId: monitor.getHandlerId(), // https://github.com/react-dnd/react-dnd/issues/2774
        }),
    }));

    return (
        <>
            {isOver && <Styled.Dimmer />}
            <Styled.SBDiv
                ref={drag}
                data-handler-id={handlerId}
                onClick={addBlock}
                onKeyDown={(e) => handleKeyPress(e, addBlock)}
                isEnabled
                hoverBorderColor={modifications.hoverBorderColor}
                isDragging={isDragging}
                onMouseEnter={() => setIsOver(true)}
                onMouseLeave={() => setIsOver(false)}
                backgroundColor={modifications.rgba}
                color={colors?.backgroundColor}
            >
                <Styled.BlockIconWrapper>
                    <Styled.SBLogo src={logo} />
                    <Styled.LineWrapper filter={modifications.filter}>
                        {[36, 29, 14].map((width) => (
                            <Styled.Line
                                key={width}
                                // @ts-ignore
                                width={width}
                                color={colors?.backgroundColor}
                            />
                        ))}
                    </Styled.LineWrapper>
                </Styled.BlockIconWrapper>
                <Styled.BlockHeading>
                    {_truncate(appName, { length: rules.MAXIMUM_QUICKBLOCK_LENGTH })} News
                </Styled.BlockHeading>
            </Styled.SBDiv>
        </>
    );
};

export default connector(DraggableSBBlock);
